/**
 * AI prefill registry (TV1-1870)
 *
 * Translates the AI assistant's *semantic* prefill intent — `(entity, field,
 * value)` — into the **real, domain-specific CDSE command** the destination
 * create store actually handles (e.g. `warehouses/form/updateField`).
 *
 * Why this lives in the web-app, next to the stores it targets:
 *   The backend capability tool (`open_create_form`) emits a generic
 *   `commandType: 'prefill'` step carrying only the logical field. It does NOT
 *   know the store's command vocabulary — that knowledge is owned here, beside
 *   the stores. When a store renames or restructures its form commands, this
 *   map is the single place that must change, and the drift-guard test
 *   (`prefillRegistry.test.ts`) fails in CI if it doesn't. Putting the map in
 *   the backend library instead would re-create the exact silent-no-op bug this
 *   ticket fixes — a hardcoded command string with no compile-time link to the
 *   store, free to rot unnoticed.
 *
 * An unsupported `(entity, field)` pair THROWS rather than being dropped: the
 * plan executor surfaces the throw as a failed step instead of marking a no-op
 * "completed". Advertising a prefill we can't honor is worse than refusing it.
 */

export class PrefillFieldUnsupportedError extends Error {
  constructor( public readonly entity: string, public readonly field: string ) {
    super( `Prefill is not supported for field "${field}" on "${entity}".` );
    this.name = 'PrefillFieldUnsupportedError';
  }
}

/** The semantic prefill step payload emitted by the backend tool. */
export type PrefillStepPayload = {
  entity: string;
  field: string;
  value: unknown;
};

/** A resolved, dispatch-ready CDSE command for the destination store. */
export type ResolvedPrefillCommand = {
  type: string;
  payload: unknown;
};

type PrefillResolver = ( field: string, value: unknown ) => ResolvedPrefillCommand;

/**
 * Per-entity resolvers. Add an entity here only when its create form is a flat,
 * single-step form whose fields are directly settable via a CDSE command.
 *
 * NOT here (and intentionally text-only): `publication`. Its create flow is an
 * item-driven 4-step wizard — category/price/stock derive from the selected
 * inventory item, and only title/description/status are directly settable, on a
 * step the user does not see from the entry route. Wizard-aware prefill is
 * tracked separately.
 */
const PREFILL_REGISTRY: Record<string, PrefillResolver> = {
  // warehouse: flat form, store `warehouses` exposes a generic field setter
  // `warehouses/form/updateField` over { field: 'label' | 'address', value }.
  // The user says "name"; the form field is `label`.
  warehouse: ( field, value ) => {
    const targetByLogicalField: Record<string, 'label' | 'address'> = {
      name: 'label',
      label: 'label',
      address: 'address'
    };
    const target = targetByLogicalField[ field ];
    if ( !target ) throw new PrefillFieldUnsupportedError( 'warehouse', field );
    return { type: 'warehouses/form/updateField', payload: { field: target, value }};
  }
};

/** Logical fields a given entity can prefill — used by tests/introspection. */
export const PREFILLABLE_ENTITIES = Object.keys( PREFILL_REGISTRY );

/**
 * Resolve a semantic prefill step into the real CDSE command for its store.
 * Throws {@link PrefillFieldUnsupportedError} for an unknown entity or an
 * unsupported field so the executor fails the step visibly.
 */
export function resolvePrefillCommand( payload: PrefillStepPayload ): ResolvedPrefillCommand {
  const resolver = PREFILL_REGISTRY[ payload.entity ];
  if ( !resolver ) throw new PrefillFieldUnsupportedError( payload.entity, payload.field );
  return resolver( payload.field, payload.value );
}
